import type { SessionTimeout } from "@/components/settings/account/types";
import { LOCAL_STORE_NAME_CONFIG } from "@/config/localStoreNameConfig";

/**
 * Account → Security "session timeout after inactivity" preference.
 *
 * The setting has no backend endpoint (see
 * docs/api-requests/question/OPEN-2026-07-18-account-profile-and-change-password.md),
 * so it lives in `localStorage` as a per-browser preference (like the theme) and
 * drives the AuthGuard idle-logout timer. `AuthGuard` renders ABOVE the Redux
 * provider, so it reads the value here (not from the store); saves emit
 * `SESSION_TIMEOUT_CHANGED_EVENT` so an open AuthGuard picks up the new value live.
 */

/** Idle duration per option, in ms. `never` disables idle logout (→ null). */
const TIMEOUT_MS: Record<SessionTimeout, number | null> = {
  "15_min": 15 * 60_000,
  "30_min": 30 * 60_000,
  "1_hour": 60 * 60_000,
  "2_hours": 2 * 60 * 60_000,
  "3_hours": 3 * 60 * 60_000,
  "4_hours": 4 * 60 * 60_000,
  never: null,
};

/** Same-tab notification that the stored preference changed (Save). */
export const SESSION_TIMEOUT_CHANGED_EVENT = "em:session-timeout-changed";

/** Matches the seeded default in `defaultAccountSettings()`. */
const DEFAULT_TIMEOUT: SessionTimeout = "1_hour";

function isSessionTimeout(v: string | null): v is SessionTimeout {
  return v !== null && v in TIMEOUT_MS;
}

/** The persisted preference, or null if none/invalid. */
export function getStoredSessionTimeout(): SessionTimeout | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(
    LOCAL_STORE_NAME_CONFIG.SESSION_TIMEOUT,
  );
  return isSessionTimeout(v) ? v : null;
}

/** Idle-logout window in ms (null = never), falling back to the default. */
export function getIdleTimeoutMs(): number | null {
  return TIMEOUT_MS[getStoredSessionTimeout() ?? DEFAULT_TIMEOUT];
}

/** Persist the preference and notify any open AuthGuard in this tab. */
export function setStoredSessionTimeout(value: SessionTimeout): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LOCAL_STORE_NAME_CONFIG.SESSION_TIMEOUT, value);
  window.dispatchEvent(new Event(SESSION_TIMEOUT_CHANGED_EVENT));
}
